import React, { useState, useEffect, useMemo } from "react";
import { Link, useLocation } from "react-router-dom";
import { CSSTransition } from 'react-transition-group';
import { Routess } from "../Routes/routes";
import { useHasPermission } from "../helpers/useApi";
import { sidebarMenu } from "../Routes/SidebarMenu";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useAuth } from "../store/AuthContext";
import { useIsMobile } from "../helpers/useIsMobile";
import ReactLogo from "../assets/img/tlogo.png";
import { faAngleDown, faAngleRight } from "@fortawesome/free-solid-svg-icons";

// Tailwind classes for sidebar
const sidebarClasses = {
  sidebar: "bg-dark border-r border-deepBlue shadow-none fixed top-0 left-0 bottom-0 overflow-hidden z-40 transition-all duration-300",
  expanded: "w-64",
  collapsed: "w-0 md:w-64 pointer-events-none md:pointer-events-auto opacity-0 md:opacity-100", // On mobile it hides, on desktop we might want minified sidebar? Orig code had collapse logic.
  // Actually original logic: effectiveCollapsed ? "collapsed" : "expanded"
  // If collapsed means HIDDEN (mobile sidebar logic often), then w-0.
  // If just minimized icon-only, that's different.
  // Inspection of orig: effectiveCollapsed ? "0px" : "260px".
  // Mobile: <768px.
};

const Sidebar = () => {
  const { isSidebarCollapsed, setIsSidebarCollapsed } = useAuth();
  const isMobile = useIsMobile();
  const location = useLocation();
  const hasPermission = useHasPermission();

  useEffect(() => {
    if (isMobile) {
      setIsSidebarCollapsed(true);
    }
  }, [location.pathname, isMobile, setIsSidebarCollapsed]);

  const filteredMenu = useMemo(() => {
    return sidebarMenu.filter(item => !item.permission || hasPermission(item.permission));
  }, [hasPermission]);

  const CollapsableNavItem = ({ eventKey, title, icon, children }) => {
    const [isOpen, setIsOpen] = useState(false);
    const isActive = location.pathname.includes(eventKey);

    useEffect(() => {
      if (isActive) {
        setIsOpen(true);
      }
    }, [isActive]);

    const handleToggle = () => {
      setIsOpen(!isOpen);
    };

    return (
      <div className="mb-1">
        <button
          onClick={handleToggle}
          className={`w-full flex items-center justify-between px-5 py-3 text-sm font-medium rounded-lg transition-colors duration-200 
            ${isActive || isOpen ? 'text-primary bg-primary/10' : 'text-gray-600 hover:text-primary hover:bg-gray-50'}`}
        >
          <div className="flex items-center">
            <span className="w-5 text-center mr-3">
              <FontAwesomeIcon icon={icon} />
            </span>
            <span>{title}</span>
          </div>
          <FontAwesomeIcon icon={isOpen ? faAngleDown : faAngleRight} className="text-xs" />
        </button>

        <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'}`}>
          <div className="pl-9 py-1 space-y-1">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const NavItem = ({ title, link, icon }) => {
    const isActive = location.pathname === link;
    return (
      <Link
        to={link}
        className={`flex items-center px-5 py-3 text-sm font-medium rounded-lg mb-1 transition-all duration-200
            ${isActive ? 'text-white bg-primary shadow-lg shadow-primary/30' : 'text-gray-600 hover:text-primary hover:bg-gray-50 hover:translate-x-1'}`}
      >
        <span className="w-5 text-center mr-3">
          <FontAwesomeIcon icon={icon} />
        </span>
        <span>{title}</span>
      </Link>
    );
  };

  const sidebarWidthClass = isSidebarCollapsed ? "-translate-x-full md:hidden" : "translate-x-0 w-64";

  return (
    <div
      className={`fixed top-0 left-0 h-full bg-white border-r border-lightGray z-50 transition-transform duration-300 ease-in-out overflow-y-auto custom-scrollbar
        ${sidebarWidthClass} md:translate-x-0 md:block ${isSidebarCollapsed ? 'md:hidden' : 'md:w-64'}`}
    >
      <div className="flex flex-col pt-5 px-3 min-h-screen">
        <Link to={Routess.DashboardOverview.path} className="flex items-center justify-center p-3 mb-6 bg-white rounded-xl shadow-sm border border-lightGray w-12 h-12 mx-auto">
          <img src={ReactLogo} alt="Logo" className="w-full h-auto" />
        </Link>

        <nav className="flex-1 space-y-1">
          {filteredMenu.map(item => (
            item.children ? (
              <CollapsableNavItem key={item.title} eventKey={item.eventKey} title={item.title} icon={item.icon}>
                {item.children.filter(child => !child.permission || hasPermission(child.permission)).map(child => (
                  <Link
                    key={child.title}
                    to={child.link}
                    className={`block py-2 px-4 text-sm rounded-r-lg border-l-2 text-gray-500 hover:text-primary hover:border-l-primary hover:bg-gray-50 transition-all duration-200
                        ${location.pathname === child.link ? 'text-primary border-l-primary bg-primary/5 font-medium' : 'border-transparent'}`}
                  >
                    {child.title}
                  </Link>
                ))}
              </CollapsableNavItem>
            ) : (
              <NavItem key={item.title} title={item.title} link={item.link} icon={item.icon} />
            )
          ))}
        </nav>
        <div className="h-10"></div>
      </div>
    </div>
  );
};

export default Sidebar;
